import { useMemo, useState, useEffect, useRef } from "react";

import { Card, CardContent } from "@/components/ui/card";

import { Button } from "@/components/ui/button";

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

import { Clock, Plus } from "lucide-react";

import { ResourceBudgetWrapper } from "@/components/blocks/budget";

import { RevisionDetailsView } from "@/components/blocks/budget/revision-details-view";

import { toast } from "sonner";

import {
  getDataMode,
  createResourceAllocation,
  createPersonBudget,
  fetchResourceBudgetIdForProject,
  fetchCostCentreBudgetsByProject,
} from "@/lib/data-service";

import { useProjectDetailsContext } from "../context";
import { useBudgetPermissions } from "@/hooks/use-budget-permissions";

import type { Employee } from "@/types";
import type { RoleBudget, ResourceBudget, BudgetRevision } from "@/types/budget.types";

export function ResourceBudgetTab() {
  const ctx = useProjectDetailsContext();
  // Destructure all needed values from context
  const {
    project, employees,
    numericProjectId,
    budgetData, budgetLoading, budgetError, enrichedBudgetData,
    setLocalBudgetData,
    resourceAllocations,
    costCentres,
    pendingRequestRoles, setRaiseRequestModalOpen,
    setRaiseRequestPrefilledRole,
    setEditResourceBudgetPageOpen,
    setBudgetExportModalOpen,
    setActiveBudgetType,
    revisionHistoryOpen, setRevisionHistoryOpen,
    setViewingRevision,
    setViewingRevisionPageOpen,
    setNewRevisionPageOpen,
    setDuplicateRevision,
    setMakeActiveRevision,
    setMakeActiveSource,
    // Additional items that may be used
    handleRestoreRevision,
  } = ctx;

  const { isCostCentreMode } = useBudgetPermissions();

  // In Cost Centre mode, fetch resource budget data from API
  const [ccResourceEntries, setCcResourceEntries] = useState<Record<number, Record<string, number>>>({});
  const ccResFetchedRef = useRef(false);
  useEffect(() => {
    if (!isCostCentreMode || !costCentres?.length || ccResFetchedRef.current) return;
    ccResFetchedRef.current = true;
    fetchCostCentreBudgetsByProject(numericProjectId, 'resource')
      .then((entries) => {
        const map: Record<number, Record<string, number>> = {};
        for (const entry of entries) {
          if (entry.monthlyBudgets) map[entry.costCentreId] = entry.monthlyBudgets;
        }
        setCcResourceEntries(map);
      })
      .catch(() => { /* fall back to localStorage */ });
  }, [isCostCentreMode, costCentres, numericProjectId]);

  // Build synthetic resource budget from API data, with localStorage fallback
  const ccModeResourceBudget = useMemo<ResourceBudget | null>(() => {
    if (!isCostCentreMode || !costCentres?.length) return null;

    const allRoles: RoleBudget[] = [];
    let roleIdCounter = -1;

    for (const cc of costCentres) {
      // Try API data first, then fall back to localStorage for detailed role data
      const apiMonthly = ccResourceEntries[cc.id ?? 0];
      try {
        const raw = localStorage.getItem(`cc-resource-budget-${numericProjectId}-${cc.code}`);
        if (!raw && !apiMonthly) continue;
        const parsed = raw ? JSON.parse(raw) as {
          roleMonthlyHours: Record<string, Record<string, number>>;
          roleNames?: Record<string, string>;
          roleRates?: Record<string, number>;
        } : null;
        if (!parsed?.roleMonthlyHours && !apiMonthly) continue;

        if (parsed?.roleMonthlyHours) {
          // Use detailed role data from localStorage (contains per-role breakdown)
          const existingRoles = enrichedBudgetData?.resourceBudget?.byRole?.filter(
            r => r.costCentreCode === cc.code
          ) ?? [];

          for (const [roleKey, monthlyMap] of Object.entries(parsed.roleMonthlyHours)) {
            const totalHours = Object.values(monthlyMap).reduce((s, h) => s + h, 0);
            if (totalHours <= 0) continue;

            const storedName = parsed.roleNames?.[roleKey];
            const storedRate = parsed.roleRates?.[roleKey];
            const existingRole = existingRoles.find(r => String(r.id) === roleKey);
            const roleName = storedName ?? existingRole?.role ?? `Role ${roleKey}`;
            const billRate = storedRate ?? existingRole?.billRate ?? 150;

            allRoles.push({
              id: existingRole?.id ?? roleIdCounter--,
              roleId: existingRole?.roleId,
              role: roleName,
              budgetHours: totalHours,
              allocatedHours: totalHours,
              actualHours: existingRole?.actualHours ?? 0,
              remainingHours: totalHours - (existingRole?.actualHours ?? 0),
              percentUsed: totalHours > 0 ? ((existingRole?.actualHours ?? 0) / totalHours) * 100 : 0,
              billRate,
              budgetCost: totalHours * billRate,
              actualCost: (existingRole?.actualHours ?? 0) * billRate,
              monthlyHours: monthlyMap,
              costCentreCode: cc.code,
            });
          }
        } else if (apiMonthly) {
          // Use API aggregate data as single role entry
          const totalHours = Object.values(apiMonthly).reduce((s, h) => s + h, 0);
          if (totalHours > 0) {
            allRoles.push({
              id: roleIdCounter--,
              role: `${cc.code} – ${cc.name}`,
              budgetHours: totalHours,
              allocatedHours: totalHours,
              actualHours: 0,
              remainingHours: totalHours,
              percentUsed: 0,
              billRate: 150,
              budgetCost: totalHours * 150,
              actualCost: 0,
              monthlyHours: apiMonthly,
              costCentreCode: cc.code,
            });
          }
        }
      } catch { /* ignore */ }
    }

    if (allRoles.length === 0) return null;

    const totalBudget = allRoles.reduce((s, r) => s + r.budgetHours, 0);
    const totalActual = allRoles.reduce((s, r) => s + r.actualHours, 0);
    const totalBudgetCost = allRoles.reduce((s, r) => s + r.budgetCost, 0);
    const totalActualCost = allRoles.reduce((s, r) => s + r.actualCost, 0);

    return {
      id: enrichedBudgetData?.resourceBudget?.id,
      projectId: numericProjectId,
      summary: {
        totalBudget,
        totalActual,
        totalRemaining: totalBudget - totalActual,
        progressPercent: totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0,
        billablePercent: enrichedBudgetData?.resourceBudget?.summary.billablePercent ?? 0,
        targetBillablePercent: enrichedBudgetData?.resourceBudget?.summary.targetBillablePercent ?? 85,
        totalBudgetCost,
        totalActualCost,
      },
      byRole: allRoles,
      byPerson: enrichedBudgetData?.resourceBudget?.byPerson ?? [],
      byPhase: enrichedBudgetData?.resourceBudget?.byPhase ?? [],
    };
  }, [isCostCentreMode, costCentres, numericProjectId, enrichedBudgetData?.resourceBudget, ccResourceEntries]);

  return (
    <>
                  {budgetLoading ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading budget data...</p>
                      </div>
                    </div>
                  ) : budgetError ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center space-y-4">
                        <div className="text-destructive mb-2">Error loading budget data</div>
                        <p className="text-sm text-muted-foreground">{budgetError}</p>
                      </div>
                    </div>
                  ) : budgetData && enrichedBudgetData?.resourceBudget ? (
                    <>
                      <ResourceBudgetWrapper
                        projectId={numericProjectId}
                        data={ccModeResourceBudget ?? enrichedBudgetData.resourceBudget}
                        onEdit={() => setEditResourceBudgetPageOpen(true)}
                        onExport={() => {
                          setActiveBudgetType("resource");
                          setBudgetExportModalOpen(true);
                        }}
                        onHistory={() => {
                          setRevisionHistoryOpen(true);
                        }}
                        availablePeople={employees}
                        existingAllocations={resourceAllocations}
                        projectStartDate={project?.startDate}
                        projectEndDate={project?.endDate}
                        onAssign={async (role, selections) => {
                          const dataMode = getDataMode();
                          const totalAssignedHours = selections.reduce((sum, s) => sum + s.allocatedHours, 0);

                          if (dataMode === "live") {
                            try {
                              const now = new Date();
                              const startDate = project?.startDate || now.toISOString().split("T")[0];
                              const endDate =
                                project?.endDate ||
                                new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

                              for (const selection of selections) {
                                const allocationData = {
                                  projectId: numericProjectId,
                                  employeeId: selection.personId,
                                  role: role,
                                  allocatedHours: selection.allocatedHours,
                                  startDate: startDate,
                                  endDate: endDate,
                                  status: "confirmed" as const,
                                };
                                await createResourceAllocation(allocationData);
                              }

                              let resourceBudgetId = enrichedBudgetData?.resourceBudget?.id;
                              if (!resourceBudgetId) {
                                console.warn(
                                  "[onAssign/ResourceBudget] resourceBudgetId not found in local state, fetching from API...",
                                );
                                resourceBudgetId =
                                  (await fetchResourceBudgetIdForProject(numericProjectId)) ?? undefined;
                              }
                              if (resourceBudgetId) {
                                                                const matchedRole = enrichedBudgetData?.resourceBudget?.byRole?.find(
                                                                  (r: RoleBudget) => r.role === role,
                                );
                                for (const selection of selections) {
                                                                    const employee = employees.find((e: Employee) => e.id === selection.personId);
                                                                    try {
                                                                      await createPersonBudget({
                                      resourceBudgetId,
                                      personId: selection.personId,
                                      personName: employee?.name || selection.personName,
                                      roleId: matchedRole?.roleId,
                                      budgetHours: selection.allocatedHours,
                                      costRate: 0,
                                    });
                                  } catch (pbError) {
                                    console.warn("Failed to create PersonBudget record:", pbError);
                                  }
                                }
                              } else {
                                console.warn(
                                  "[onAssign/ResourceBudget] No resourceBudgetId available — skipping PersonBudget creation",
                                );
                              }
                            } catch (error) {
                              console.error("Failed to create resource allocations:", error);
                              toast.error("Failed to assign team members to project");
                              return;
                            }
                          }

                          if (enrichedBudgetData?.resourceBudget?.byRole) {
                            const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map((r: RoleBudget) => {
                              if (r.role === role) {
                                return { ...r, allocatedHours: r.allocatedHours + totalAssignedHours };
                              }
                              return r;
                            });

                            const newPersonBudgets = selections.map((selection) => {
                                                            const employee = employees.find((e: Employee) => e.id === selection.personId);
                                                            return {
                                id: -Date.now() - selection.personId,
                                personId: selection.personId,
                                personName: employee?.name || selection.personName,
                                role: role,
                                budgetHours: selection.allocatedHours,
                                actualHours: 0,
                                utilisation: 0,
                                costRate: 0,
                                budgetCost: 0,
                                actualCost: 0,
                              };
                            });

                            const existingByPerson = enrichedBudgetData.resourceBudget.byPerson || [];

                            setLocalBudgetData({
                              ...enrichedBudgetData,
                              resourceBudget: {
                                ...enrichedBudgetData.resourceBudget,
                                byRole: updatedRoles,
                                byPerson: [...existingByPerson, ...newPersonBudgets],
                              },
                            });
                          }

                          toast.success(
                            `Assigned ${selections.length} team member${selections.length !== 1 ? "s" : ""} to ${role}`,
                          );
                        }}
                        onRaiseRequest={(roleName) => {
                          setRaiseRequestPrefilledRole(roleName);
                          setRaiseRequestModalOpen(true);
                        }}
                        pendingRequestRoles={pendingRequestRoles}
                      />
                      <Sheet open={revisionHistoryOpen} onOpenChange={setRevisionHistoryOpen}>
                        <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto">
                          <SheetHeader className="px-6 pt-6">
                            <SheetTitle>Revision History</SheetTitle>
                            <SheetDescription>
                              View, compare and restore previous versions of the resource budget.
                            </SheetDescription>
                          </SheetHeader>
                          <div className="mt-6 px-6 pb-6">
                            <RevisionDetailsView
                              budgetLayer="resource"
                              revisions={enrichedBudgetData?.revisions ?? []}
                              projectName={project?.name ?? ""}
                              onViewRevision={(id) => {
                                                                const rev = (enrichedBudgetData?.revisions ?? []).find((r: BudgetRevision) => r.id === id || r.revisionNumber === id);
                                                                if (rev) {
                                                                  setViewingRevision(rev);
                                                                  setViewingRevisionPageOpen(true);
                                }
                                setRevisionHistoryOpen(false);
                              }}
                              onRestore={(id) => {
                                handleRestoreRevision(String(id));
                              }}
                              onMakeActive={(id) => {
                                                                const rev = (enrichedBudgetData?.revisions ?? []).find((r: BudgetRevision) => r.id === id);
                                                                if (rev) {
                                                                  setMakeActiveSource('make-active');
                                  setMakeActiveRevision(rev);
                                }
                              }}
                              onNewRevision={() => {
                                setRevisionHistoryOpen(false);
                                setNewRevisionPageOpen(true);
                              }}
                              onDuplicate={(id) => {
                                                                const rev = (enrichedBudgetData?.revisions ?? []).find((r: BudgetRevision) => r.id === id || r.revisionNumber === id);
                                                                if (rev) {
                                                                  setRevisionHistoryOpen(false);
                                                                  setDuplicateRevision(rev);
                                }
                              }}
                            />
                          </div>
                        </SheetContent>
                      </Sheet>

                    </>
                  ) : (
                    <div className="flex items-center justify-center min-h-[500px]">
                      <Card className="w-full max-w-md border-dashed">
                        <CardContent className="flex flex-col items-center justify-center text-center p-12">
                          <div className="rounded-full bg-muted p-4 mb-4">
                            <Clock className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <h3 className="text-lg font-semibold mb-2">No Resource Budget</h3>
                          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                            Plan team capacity and track hours by creating a resource budget for this project.
                          </p>
                          <Button onClick={() => setEditResourceBudgetPageOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Resource Budget
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  )}

    </>
  );
}
